/**
 * Fan pattern detection.
 *
 * Split in two: limit hands are recognised from the raw tiles before any
 * decomposition (several of them are not 4-sets-and-a-pair at all), and the
 * regular patterns are read off a chosen decomposition.
 */

import {
  type Tile,
  WIND_TILE,
  bonusKindOf,
  bonusWindOf,
  countTiles,
  isDragon,
  isHonour,
  isTerminalOrHonour,
  isWind,
  rankOf,
  suitOf,
} from '../tiles';
import type { HandInput, Meld, PatternHit, WinContext } from '../types';
import type { Decomposition } from '../shared/decompose';
import { activeFanCap, DEFAULT_SETTINGS, type HkosSettings } from './settings';

/** Every tile in the hand, concealed plus melded. */
export function allTiles(hand: HandInput): Tile[] {
  return [...hand.concealed, ...hand.melds.flatMap((m) => m.tiles)];
}

/** No claimed set at all — neither a non-concealed meld nor the `claimedSet` flag. */
function isFullyConcealed(hand: HandInput): boolean {
  return hand.melds.every((m) => m.concealed) && !hand.claimedSet;
}

// ---------------------------------------------------------------------------
// Limit hands
// ---------------------------------------------------------------------------

const THIRTEEN_ORPHAN_TILES: Tile[] = [
  '1m', '9m', '1p', '9p', '1s', '9s', '1z', '2z', '3z', '4z', '5z', '6z', '7z',
];

/**
 * Thirteen Orphans is checked separately because it is the one limit hand
 * that is NOT four sets and a pair — it must be recognised before the
 * decomposer rejects the hand as an invalid shape.
 */
export function detectThirteenOrphans(
  hand: HandInput,
  settings: HkosSettings,
): PatternHit | undefined {
  const tiles = allTiles(hand);
  const counts = countTiles(tiles);
  const fullyConcealed = isFullyConcealed(hand);

  const matches =
    tiles.length === 14 &&
    fullyConcealed &&
    counts.size === 13 &&
    THIRTEEN_ORPHAN_TILES.every((t) => (counts.get(t) ?? 0) >= 1);

  return matches
    ? { key: 'thirteenOrphans', label: 'Thirteen Orphans', fan: activeFanCap(settings), limit: true }
    : undefined;
}

const SEVEN_PAIRS_COUNT = 7;

/**
 * Raw shape check for Seven Pairs and Seven Shifted Pairs: 14 concealed
 * tiles that split cleanly into 7 pairs, no melds of any kind (a kong would
 * turn one "pair" into four-of-a-kind, which isn't a pair anymore, so any
 * meld at all, kong included, disqualifies the shape — see
 * `sevenPairsBlockedByKong` for that explicit rejection).
 *
 * Confirmed house rule 2026-09-17: two pairs of the *same* tile (four
 * physical copies, held as a plain quadruplet rather than declared as a
 * kong) count as two of the seven pairs — this engine doesn't require all
 * seven to be distinct tiles. Only a genuine kong (an explicit meld) breaks
 * the pairs structure; four identical concealed tiles left undeclared do
 * not. Every distinct tile's count must therefore be even (2 or 4 — 4 is
 * the physical maximum, odd counts can't split into pairs at all) and the
 * pair counts must total exactly seven.
 *
 * Returns one entry per pair (so a four-of-a-kind contributes its tile
 * twice), not one entry per distinct tile — callers that care about
 * distinctness (`isShiftedRun`'s consecutive-rank check) already get the
 * right answer from that: a repeated tile can never be part of a shifted
 * run, since a run needs 7 different ranks.
 */
function sevenPairsShape(hand: HandInput): Tile[] | undefined {
  if (hand.melds.length > 0 || hand.claimedSet) return undefined;
  if (hand.concealed.length !== 14) return undefined;
  const counts = countTiles(hand.concealed);
  const pairTiles: Tile[] = [];
  for (const [tile, n] of counts) {
    if (n !== 2 && n !== 4) return undefined;
    for (let i = 0; i < n / 2; i++) pairTiles.push(tile);
  }
  if (pairTiles.length !== SEVEN_PAIRS_COUNT) return undefined;
  return pairTiles;
}

/**
 * True if the hand is a Seven Pairs shape EXCEPT that one of the pairs was
 * upgraded to a kong — confirmed house rule 2026-09-10: not allowed. A kong
 * turns a 2-tile pair into 4-of-a-kind, which isn't a pair anymore, so this
 * is rejected at validation rather than silently scored some other way (the
 * concealed tiles alone, minus the kong, can't form any other valid winning
 * shape either — they're all pairs, no triplets/runs to decompose into).
 */
export function sevenPairsBlockedByKong(hand: HandInput): boolean {
  if (hand.melds.length === 0 || hand.claimedSet) return false;
  if (!hand.melds.every((m) => m.type === 'kong')) return false;
  const kongTiles = hand.melds.map((m) => m.tiles[0]!);
  const kongSet = new Set(kongTiles);
  if (kongSet.size !== kongTiles.length) return false;
  const concealedCounts = countTiles(hand.concealed);
  if (![...concealedCounts.values()].every((c) => c === 2)) return false;
  return concealedCounts.size + kongSet.size === SEVEN_PAIRS_COUNT;
}

/**
 * True if the hand reduces cleanly to "one pair + N concealed pungs" from
 * the raw tile counts — Self Triplets' shape. Unlike the honour-triplet/
 * dragon/wind patterns in `detectPatterns`, this doesn't need a chosen
 * `Decomposition` from `parse.ts`: if every distinct tile value's count is
 * exactly 2 (the pair — only one such value allowed) or 3 (a concealed
 * pung), plus whatever kongs are already declared in `hand.melds`, that's
 * the only way to read the hand at all — no chow is possible under that
 * count shape, so there's no decomposition ambiguity to resolve.
 */
function selfTripletsShape(hand: HandInput): { pairTile: Tile } | undefined {
  if (!isFullyConcealed(hand)) return undefined;
  const kongCount = hand.melds.filter((m) => m.type === 'kong').length;
  const setsNeeded = 4 - kongCount;
  const counts = countTiles(hand.concealed);
  let pairTile: Tile | undefined;
  let pungCount = 0;
  for (const [tile, n] of counts) {
    if (n === 2) {
      if (pairTile) return undefined; // two candidate pairs — not this shape
      pairTile = tile;
    } else if (n === 3) {
      pungCount++;
    } else {
      return undefined; // count 1 or 4+ (outside a declared kong) — not this shape
    }
  }
  if (!pairTile || pungCount !== setsNeeded) return undefined;
  return { pairTile };
}

/** True if the 7 pair-tiles are all one numbered suit and their ranks form one unbroken run (e.g. 1-7). */
function isShiftedRun(pairTiles: Tile[]): boolean {
  const suits = new Set(pairTiles.map(suitOf));
  if (suits.size !== 1 || suits.has('z')) return false;
  const ranks = pairTiles.map(rankOf).sort((a, b) => a - b);
  return ranks.every((r, i) => i === 0 || r === ranks[i - 1]! + 1);
}

/**
 * Seven Shifted Pairs — seven pairs in one suit, each rank one up from the
 * last (e.g. 11 22 33 44 55 66 77 of dots). MCR ties this with Thirteen
 * Orphans at its top point tier (88 each), so it gets the same treatment
 * here: a forced limit hand, checked before the decomposer since — like
 * Thirteen Orphans — it isn't a standard 4-sets-and-a-pair shape.
 */
export function detectSevenShiftedPairs(
  hand: HandInput,
  settings: HkosSettings,
): PatternHit | undefined {
  const pairTiles = sevenPairsShape(hand);
  if (!pairTiles || !isShiftedRun(pairTiles)) return undefined;
  return { key: 'sevenShiftedPairs', label: 'Seven Shifted Pairs', fan: activeFanCap(settings), limit: true };
}

/**
 * Seven Pairs — seven pairs of any tiles (mixed suits, or one suit without
 * the shifted-run requirement above). An ordinary pattern, not a forced
 * limit: it stacks with Full Flush/Half Flush/Concealed Hand/Self-Draw/
 * flowers exactly like every other pattern here — this engine has no
 * pattern-exclusion logic anywhere, and Seven Pairs isn't special-cased to
 * be the first one (confirmed house-rule call, not an oversight: a same-suit
 * Seven Pairs hand is meant to also earn Full Flush on top). Also stacks
 * with All Honours (confirmed 2026-09-10) whenever all 7 pairs happen to be
 * honour tiles (winds/dragons) — since a duplicated pair counts double
 * (2026-09-17, see `sevenPairsShape`), this no longer requires all 7 of the
 * distinct honour tile types to be present, just that every pair is one.
 * Never stacks with anything kong-related: the shape
 * check above already requires zero melds, so a kong can't coexist with
 * Seven Pairs at all — see `sevenPairsBlockedByKong` for the explicit
 * rejection when a player tries to.
 */
export function detectSevenPairs(
  hand: HandInput,
  ctx: WinContext,
  settings: HkosSettings,
): PatternHit[] | undefined {
  const pairTiles = sevenPairsShape(hand);
  if (!pairTiles || isShiftedRun(pairTiles)) return undefined;
  const hits: PatternHit[] = [{ key: 'sevenPairs', label: 'Seven Pairs', fan: settings.fan.sevenPairs }];
  if (pairTiles.every(isHonour) && settings.fan.allHonours > 0) {
    hits.push({ key: 'allHonours', label: 'All Honours', fan: settings.fan.allHonours });
  }
  // Confirmed 2026-09-22: Fully Concealed Hand no longer stacks with Seven
  // Pairs (reversed from the 2026-09-05 decision). Nine Gates and Self
  // Triplets never reached this code at all — they're forced limit hands
  // handled in `detectLimitHands`, which short-circuits before contextual
  // patterns are ever detected — so only Seven Pairs needed this change.
  hits.push(...detectContextualPatterns(hand, ctx, settings, { suppressConcealedHand: true }));
  return hits;
}

/** Shape-only check (no settings/context needed) for `isCompleteHand`, mirroring Thirteen Orphans' role there. */
export function isSevenPairsShape(hand: HandInput): boolean {
  return sevenPairsShape(hand) !== undefined;
}

/**
 * Heavenly Hand / Earthly Hand — forced limit hands about *how the deal
 * happened*, not the tile shape, so they're driven entirely by `ctx` rather
 * than the hand's tiles. Checked separately from `detectLimitHands` (which
 * only ever sees `hand`) and gated on `isCompleteHand` by the caller, since
 * neither flag says anything about whether the tiles actually form a valid
 * winning shape.
 */
export function detectHeavenlyEarthlyHand(
  ctx: WinContext,
  settings: HkosSettings,
): PatternHit | undefined {
  const limitFan = activeFanCap(settings);
  if (settings.toggles.heavenlyHand && ctx.heavenlyHand) {
    return { key: 'heavenlyHand', label: 'Heavenly Hand', fan: limitFan, limit: true };
  }
  if (settings.toggles.earthlyHand && ctx.earthlyHand) {
    return { key: 'earthlyHand', label: 'Earthly Hand', fan: limitFan, limit: true };
  }
  return undefined;
}

export type FixedLimitKey = 'allHonours' | 'orphans' | 'nineGates' | 'selfTriplets';

/**
 * The fan value of a fixed-value limit hand. A game saved before Orphans /
 * Nine Gates / Self Triplets had their own values carries a settings snapshot
 * without them, so fall back to the shipped default rather than score NaN.
 */
export function fixedLimitFan(settings: HkosSettings, key: FixedLimitKey): number {
  return settings.fan[key] ?? DEFAULT_SETTINGS.fan[key];
}

/**
 * Limit hands that DO take the standard four-sets-and-a-pair shape. Only call
 * this once the hand is known to decompose validly.
 *
 * Confirmed 2026-09-11: All Honours, Orphans, Nine Gates, Self Triplets, and
 * All Kongs moved (back) here from `detectPatterns`, reversing the
 * 2026-09-09/2026-09-10 "ordinary fixed-fan pattern" reclassification — Indra
 * pointed out the scoring is meant to follow
 * https://en.wikipedia.org/wiki/Hong_Kong_mahjong_scoring_rules, which lists
 * all of these as genuine limit hands, "ineligible for additional faan
 * awarded based on winds, dragons, or flowers." Each now short-circuits like
 * Great Winds always has — a hand matching one of these scores ONLY that
 * pattern, full stop, regardless of what else it might also look like. See
 * CLAUDE.md for the full history (this reverses a deliberate earlier
 * decision, not a bug fix).
 *
 * Values (confirmed 2026-09-19, same Wikipedia page): All Honours, Orphans,
 * Nine Gates and Self Triplets are fixed at 10 faan (`settings.fan`), so they
 * pay 10 — bounded by the mode's cap — not the cap itself; Great Winds and All
 * Kongs are the 13-faan tier and pay the cap. When more than one matches, the
 * caller keeps the most valuable.
 */
export function detectLimitHands(
  hand: HandInput,
  ctx: WinContext,
  settings: HkosSettings,
): PatternHit[] {
  const hits: PatternHit[] = [];
  const limitFan = activeFanCap(settings);
  // Two tiers, both from Wikipedia's Hong Kong scoring page (confirmed
  // 2026-09-19): Great Winds / All Kongs (and Thirteen Orphans / Seven Shifted
  // Pairs elsewhere) pay the table's cap; All Honours / Orphans / Nine Gates /
  // Self Triplets are fixed-value hands (10 faan by default) that take no
  // additional faan. Neither tier stacks with anything — the caller scores
  // only the single best hit — but the fixed ones are NOT flagged `limit`:
  // they only become a "limit" hand if their value reaches the cap.
  const add = (key: string, label: string) =>
    hits.push({ key, label, fan: limitFan, limit: true });
  const addFixed = (key: FixedLimitKey, label: string) =>
    hits.push({ key, label, fan: fixedLimitFan(settings, key) });

  // Great Winds (formerly "Big Four Winds", renamed 2026-09-09) — a pung/kong
  // of every wind. Key kept as `greatWinds` going forward; old persisted
  // round records keep whatever key/label was current when they were scored.
  const windSets = countTripletsOf(hand, ['1z', '2z', '3z', '4z']);
  if (windSets === 4) add('greatWinds', 'Great Winds');

  const tiles = allTiles(hand);
  if (tiles.length > 0 && tiles.every((t) => !isHonour(t) && isTerminalOrHonour(t))) {
    addFixed('orphans', 'Orphans');
  }
  if (tiles.length > 0 && tiles.every(isHonour)) {
    addFixed('allHonours', 'All Honours');
  }
  if (isFullyConcealed(hand) && hand.melds.length === 0 && isNineGates(tiles)) {
    addFixed('nineGates', 'Nine Gates');
  }
  // All 4 sets are kongs: kongs are always explicit in `hand.melds` (the
  // decomposer never infers one from `concealed`), so this is fully
  // determinable without calling `decompose` — 4 kong melds plus a 2-tile
  // concealed pair is the only shape that qualifies.
  if (hand.melds.length === 4 && hand.melds.every((m) => m.type === 'kong') && hand.concealed.length === 2) {
    add('allKongs', 'All Kongs');
  }

  const selfTripl = selfTripletsShape(hand);
  if (selfTripl) {
    const discardFormedPair = selfTripl.pairTile === hand.winningTile;
    if (ctx.selfDraw || discardFormedPair) {
      addFixed('selfTriplets', 'Self Triplets');
    }
  }

  return hits;
}

/** Counts pungs/kongs of the given tiles, whether melded or in hand. */
function countTripletsOf(hand: HandInput, targets: Tile[]): number {
  let n = 0;
  for (const target of targets) {
    const inMelds = hand.melds.some(
      (m) => (m.type === 'pung' || m.type === 'kong') && m.tiles[0] === target,
    );
    const inHand = (countTiles(hand.concealed).get(target) ?? 0) >= 3;
    if (inMelds || inHand) n++;
  }
  return n;
}

function isNineGates(tiles: Tile[]): boolean {
  if (tiles.length !== 14) return false;
  const suit = suitOf(tiles[0]!);
  if (suit === 'z') return false;
  if (!tiles.every((t) => suitOf(t) === suit)) return false;

  const ranks = new Array<number>(10).fill(0);
  for (const t of tiles) {
    const r = rankOf(t);
    ranks[r] = (ranks[r] ?? 0) + 1;
  }

  const required = [0, 3, 1, 1, 1, 1, 1, 1, 1, 3];
  let extras = 0;
  for (let r = 1; r <= 9; r++) {
    const diff = ranks[r]! - required[r]!;
    if (diff < 0) return false;
    extras += diff;
  }
  return extras === 1;
}

// ---------------------------------------------------------------------------
// Regular patterns
// ---------------------------------------------------------------------------

/**
 * Patterns that depend only on the whole tile pool and the win context — not
 * on how the hand decomposes into sets. Shared between the standard
 * sets-and-a-pair path and Seven Pairs, which has no sets at all.
 */
function detectContextualPatterns(
  hand: HandInput,
  ctx: WinContext,
  settings: HkosSettings,
  options: { suppressConcealedHand?: boolean } = {},
): PatternHit[] {
  const hits: PatternHit[] = [];
  const { fan, toggles } = settings;
  const add = (key: string, label: string, value: number) => {
    if (value > 0) hits.push({ key, label, fan: value });
  };

  const tiles = allTiles(hand);

  // --- flushes
  const suits = new Set(tiles.map(suitOf));
  const numberedSuits = new Set([...suits].filter((s) => s !== 'z'));
  if (numberedSuits.size === 1 && !suits.has('z')) {
    add('fullFlush', 'Full Flush', fan.fullFlush);
  } else if (numberedSuits.size === 1 && suits.has('z')) {
    add('halfFlush', 'Half Flush', fan.halfFlush);
  }

  // --- concealment
  // Every meld concealed AND nothing claimed from a discard.
  const fullyConcealed = isFullyConcealed(hand);
  if (fullyConcealed && !options.suppressConcealedHand) {
    add('concealedHand', 'Fully Concealed Hand', fan.concealedHand);
  }
  if (ctx.selfDraw) {
    add('selfDraw', 'Self-Draw', fan.selfDraw);
  }

  // --- flowers
  // Flat per-tile bonus and the seat/round-matching bonus stack — a flower
  // that matches your seat still counts toward the flat bonus AND earns the
  // match bonus on top, per house convention. Each is its own toggle so
  // either can be enabled independently, same pattern as wind pungs below.
  const flowers = hand.flowers ?? [];
  if (toggles.flowerBonus && flowers.length > 0) {
    hits.push({
      key: 'flowers',
      label: `Flowers ×${flowers.length}`,
      fan: fan.flower * flowers.length,
    });
  }
  // Holding all 4 of a kind always includes the one matching your own seat
  // wind, so All Flowers/All Seasons replaces the seatFlowerBonus for that
  // one tile rather than stacking with it — per house convention.
  const heldFlowerCount = flowers.filter((bt) => bonusKindOf(bt) === 'flower').length;
  const heldSeasonCount = flowers.filter((bt) => bonusKindOf(bt) === 'season').length;
  const hasAllFlowers = toggles.allFlowers && heldFlowerCount === 4;
  const hasAllSeasons = toggles.allSeasons && heldSeasonCount === 4;
  if (hasAllFlowers) {
    add('allFlowers', 'All Flowers', fan.allFlowers);
  }
  if (hasAllSeasons) {
    add('allSeasons', 'All Seasons', fan.allSeasons);
  }
  for (const bt of flowers) {
    const wind = bonusWindOf(bt);
    const suppressedByAllSet = (bonusKindOf(bt) === 'flower' && hasAllFlowers) || (bonusKindOf(bt) === 'season' && hasAllSeasons);
    if (toggles.seatFlowerBonus && wind === ctx.seatWind && !suppressedByAllSet) {
      add('seatFlowerBonus', `Seat Flower (${bt})`, fan.seatFlowerBonus);
    }
  }
  // Mirror image of the flat bonus, never both: rewards holding none at all
  // instead of rewarding however many you happen to hold.
  if (toggles.noFlowerBonus && flowers.length === 0) {
    add('noFlowerBonus', 'No Flowers', fan.noFlowerBonus);
  }

  // --- circumstantial
  if (toggles.robbingKong && ctx.robbingKong) {
    add('robbingKong', 'Robbing a Kong', fan.robbingKong);
  }
  if (toggles.kongReplacement && ctx.kongReplacement) {
    add('kongReplacement', 'Kong Replacement Win', fan.kongReplacement);
  }
  // Alternative description of a kong-replacement win (the replacement kong
  // was itself made from an earlier kong's replacement tile) — scored
  // instead of Kong Replacement Win, not on top of it; see the doc comment
  // on `WinContext.winDoubleKong`.
  if (toggles.winDoubleKong && ctx.winDoubleKong) {
    add('winDoubleKong', 'Win Double Kong', fan.winDoubleKong);
  }
  if (toggles.lastWallTile && ctx.lastWallTile) {
    add('lastWallTile', 'Last Wall Tile', fan.lastWallTile);
  }
  if (toggles.lastDiscard && ctx.lastDiscard) {
    add('lastDiscard', 'Last Discard', fan.lastDiscard);
  }

  return hits;
}

export function detectPatterns(
  hand: HandInput,
  decomp: Decomposition,
  ctx: WinContext,
  settings: HkosSettings,
): PatternHit[] {
  const hits: PatternHit[] = [];
  const { fan, toggles } = settings;
  const add = (key: string, label: string, value: number) => {
    if (value > 0) hits.push({ key, label, fan: value });
  };

  const sets = decomp.sets;
  const isTriplet = (m: Meld) => m.type === 'pung' || m.type === 'kong';
  const tiles = allTiles(hand);
  const pairTile = decomp.pair.tiles[0]!;

  // --- honour triplets (computed early: Small Winds needs to be known
  // before the structural All Pungs/All Honours checks below, so it can
  // suppress them rather than stack)
  const dragonMelds: Tile[] = [];
  const windMelds: Tile[] = [];
  for (const m of sets.filter(isTriplet)) {
    const t = m.tiles[0]!;
    if (isDragon(t)) {
      dragonMelds.push(t);
    }
    if (isWind(t)) {
      windMelds.push(t);
    }
    if (toggles.seatWindPung && t === WIND_TILE[ctx.seatWind]) {
      add('seatWindPung', 'Seat Wind Pung', fan.seatWindPung);
    }
    if (toggles.prevailingWindPung && t === WIND_TILE[ctx.prevailingWind]) {
      add('prevailingWindPung', 'Prevailing Wind Pung', fan.prevailingWindPung);
    }
  }

  // Great Dragons (3 dragon pungs/kongs) and Small Dragons (2 dragon
  // pungs/kongs + a pair of the 3rd) are each scored as their own flat fan
  // INSTEAD of the individual Dragon Pung fan those same melds would
  // otherwise earn — confirmed house rule 2026-09-10, reversing the earlier
  // "stacks on top of dragonPung" behaviour. A dragon pung that isn't part
  // of either pattern (just 1 on its own, or 2 without the matching pair)
  // still scores Dragon Pung individually, per meld. Also confirmed
  // 2026-09-10: neither Great nor Small Dragons stacks with All Pungs below
  // either (e.g. 3 dragon pungs + 1 more pung + a pair would otherwise also
  // earn All Pungs on top of Great Dragons). NOTE: All Kongs and All
  // Honours used to also interact with this section, but both moved to
  // `detectLimitHands` as short-circuiting limit hands (confirmed 2026-09-11) — a
  // hand that would trigger either never reaches this function at all
  // anymore, so there's nothing left here to suppress against them.
  const dragonSet = new Set(dragonMelds);
  const dragonPatternFired = dragonSet.size === 3 || (dragonSet.size === 2 && isDragon(pairTile) && !dragonSet.has(pairTile));
  if (dragonSet.size === 3) {
    add('greatDragons', 'Great Dragons', fan.greatDragons);
  } else if (dragonPatternFired) {
    add('smallDragons', 'Small Dragons', fan.smallDragons);
  } else {
    for (const t of dragonMelds) {
      add('dragonPung', `Dragon Pung (${t})`, fan.dragonPung);
    }
  }

  // Small Winds — 3 wind pungs/kongs + a pair of the 4th wind. Confirmed
  // house rule 2026-09-10: this is the hand's entire "every set is a
  // triplet" credit when it fires — it suppresses All Pungs below rather
  // than stacking with it (same non-stacking treatment as Great/Small
  // Dragons above). Still stacks with Half Flush, per the original spec —
  // that's a flush category, not a "some/all sets are triplets" overlap.
  const windSet = new Set(windMelds);
  const smallWindsFired = windSet.size === 3 && isWind(pairTile) && !windSet.has(pairTile);
  if (smallWindsFired) {
    add('smallWinds', 'Small Winds', fan.smallWinds);
  }

  const suppressGenericStructure = dragonPatternFired || smallWindsFired;

  // --- structure
  if (sets.every((m) => m.type === 'chow')) {
    add('allSequences', 'All Sequences', fan.allSequences);
  }
  if (sets.every(isTriplet) && !suppressGenericStructure) {
    add('allPungs', 'All Pungs', fan.allPungs);
  }

  // Mixed Orphans — added 2026-09-11: every tile is a terminal OR an
  // honour, with at least one of each (a genuine mix — the "pure" cases,
  // Orphans and All Honours, are 10-faan limit hands handled in
  // `detectLimitHands` and never reach this function). No chow is possible
  // under this tile restriction, so the hand is always also All Pungs —
  // this is deliberately the pattern's own fan only, meant to stack
  // additively with `allPungs` above (1 + 3 = 4 total), not the
  // replace-the-generic-pattern treatment used for Great/Small Dragons/
  // Small Winds.
  if (
    tiles.length > 0 &&
    tiles.every(isTerminalOrHonour) &&
    tiles.some(isHonour) &&
    tiles.some((t) => !isHonour(t)) &&
    !suppressGenericStructure
  ) {
    add('mixedOrphans', 'Mixed Orphans', fan.mixedOrphans);
  }

  hits.push(...detectContextualPatterns(hand, ctx, settings));

  return hits;
}

export function totalFan(hits: PatternHit[]): number {
  return hits.reduce((sum, h) => sum + h.fan, 0);
}
